import { useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { Download, Loader2 } from 'lucide-react';
import { SYNC_UTMB_RESULTS } from '@/graphql/mutations';

interface SyncUtmbResultsModalProps {
    raceId: string;
    onClose: () => void;
    onSuccess: () => void;
    toast: (message: string, type: 'success' | 'error' | 'info') => void;
}

export function SyncUtmbResultsModal({ raceId, onClose, onSuccess, toast }: SyncUtmbResultsModalProps) {
    const { t } = useTranslation('races');
    const { t: tc } = useTranslation();
    const isSubmitting = useRef(false);

    const [syncUtmbResults, { loading }] = useMutation(SYNC_UTMB_RESULTS);

    const handleSync = useCallback(async () => {
        if (isSubmitting.current) return;
        isSubmitting.current = true;

        try {
            const { data } = await syncUtmbResults({
                variables: { input: { raceId } },
            });

            const res = data?.syncUtmbResults;
            if (res) {
                if (res.updated === 0) {
                    toast(t('details.syncResultsModal.noUpdates'), 'info');
                } else {
                    toast(t('details.syncResultsModal.success', { updated: res.updated }), 'success');
                    onSuccess();
                }
            }
        } catch {
            toast(t('details.syncResultsModal.error'), 'error');
        } finally {
            isSubmitting.current = false;
            onClose();
        }
    }, [raceId, syncUtmbResults, onSuccess, onClose, toast, t]);

    return createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div
                className="absolute inset-0 bg-slate-900/20 backdrop-blur-[2px] animate-in fade-in duration-200"
                onClick={loading ? undefined : onClose}
            />

            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm relative z-10 p-6 animate-in zoom-in-95 duration-200 border border-slate-100">
                <div className="flex flex-col items-center text-center gap-4">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center mb-2 bg-emerald-50 text-emerald-600">
                        {loading ? <Loader2 size={24} className="animate-spin" /> : <Download size={24} />}
                    </div>

                    <h3 className="text-lg font-bold text-slate-900">
                        {t('details.syncResultsModal.title')}
                    </h3>

                    {loading ? (
                        <p className="text-sm text-slate-500">{t('details.syncResultsModal.syncing')}</p>
                    ) : (
                        <div className="space-y-3 w-full">
                            <p className="text-sm text-slate-500">
                                {t('details.syncResultsModal.message')}
                            </p>
                            <div className="flex gap-3 w-full mt-2">
                                <button
                                    onClick={onClose}
                                    className="flex-1 py-3 px-4 rounded-xl font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors active:scale-95"
                                >
                                    {tc('btn.cancel')}
                                </button>
                                <button
                                    onClick={handleSync}
                                    className="flex-1 py-3 px-4 rounded-xl font-bold text-white bg-emerald-600 hover:bg-emerald-700 transition-colors shadow-lg shadow-emerald-200 active:scale-95"
                                >
                                    {tc('btn.confirm')}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
}
